// Pantalla detalle de Revision de Relacion (rol revisor).
// Muestra el resumen de la relacion + listado de gastos como en MiBolsa.
// Acciones por fila: SOLO editar tercero (no precios).
// Acciones del cierre: Verificar (dry-run) y Aprobar+Sincronizar inmediato.
//
// Tras aprobar muestra un panel con el resultado por comprobante:
// cuales sincronizaron OK y cuales fallaron, con el error del ERP.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container, Row, Col, Card, Table, Button, Badge, Alert, Modal, Spinner, Form
} from 'react-bootstrap';
import {
  FaClipboardCheck, FaArrowLeft, FaCheckCircle, FaTimesCircle, FaExclamationTriangle,
  FaUserEdit, FaPlayCircle, FaCheck,
  FaArrowDown, FaArrowUp, FaExchangeAlt, FaWallet
} from 'react-icons/fa';
import { Alertassw } from '../Alertas/Alertassw';
import { manejarConfigFaltanteSi422 } from '../Alertas/swalConfigContableFaltante';
import {
  obtenerRelacion, previewRelacion, aprobarRelacion, verificarRelacion, cambiarTerceroGasto
} from '../../api/finanzas';
import { extractError } from './utils/extractError';
import SelectorProveedorTercero from './SelectorProveedorTercero';
import { useAuth } from '../../hooks/useAuth';
import { APPROVE_RELACIONES } from '../Roles/ConstantRoute';

const formatoCOP = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

const ESTADO_BADGE = {
  BORRADOR: 'warning',
  APROBADA: 'info',
  SINCRONIZANDO: 'info',
  SINCRONIZADA: 'success',
  SYNC_ERROR: 'danger',
  CANCELADA: 'secondary'
};

const RevisionRelacionDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { userData } = useAuth();

  const [loading, setLoading] = useState(true);
  const [relacion, setRelacion] = useState(null);
  const [gastos, setGastos] = useState([]);
  const [traslados, setTraslados] = useState([]);
  const [entregas,  setEntregas]  = useState([]);
  const [saldoAnterior, setSaldoAnterior] = useState(0);

  // Modal cambiar tercero
  const [gastoEditando, setGastoEditando] = useState(null);
  const [terceroForm, setTerceroForm] = useState({ proveedor: null, proveedorSinCrear: null });
  const [savingTercero, setSavingTercero] = useState(false);

  // Verificar (dry-run)
  const [verificando, setVerificando] = useState(false);
  const [reporteVerif, setReporteVerif] = useState(null);

  // Aprobar + sync inmediato
  const [aprobando, setAprobando] = useState(false);
  const [resultadoAprobacion, setResultadoAprobacion] = useState(null);

  useEffect(() => { cargar(); }, [id]);

  const cargar = async () => {
    setLoading(true);
    try {
      const [rel, prev] = await Promise.all([obtenerRelacion(id), previewRelacion(id)]);
      if (rel?.status === 200) setRelacion(rel.data.data);
      if (prev?.status === 200) {
        const d = prev.data.data || {};
        const porMes = d.gastosPorMes || {};
        // Flatten: tomar todos los gastos de todos los meses
        const flat = Object.values(porMes).flatMap(m => m.gastos || []);
        setGastos(flat);
        setTraslados(d.traslados || []);
        setEntregas(d.entregas   || []);
        setSaldoAnterior(d.saldoAnterior || 0);
      }
    } catch (err) {
      Alertassw('error', 'Error', 'No se pudo cargar la relación');
    } finally {
      setLoading(false);
    }
  };

  const abrirEdicionTercero = (gasto) => {
    setGastoEditando(gasto);
    setTerceroForm({
      proveedor: gasto.proveedor || null,
      proveedorSinCrear: gasto.proveedorSinCrear || null
    });
  };

  const guardarTercero = async () => {
    if (!gastoEditando) return;
    const tieneProv = !!terceroForm.proveedor;
    const tieneManual = !!(terceroForm.proveedorSinCrear?.nombre && terceroForm.proveedorSinCrear?.nit);
    if (!tieneProv && !tieneManual) {
      Alertassw('warning', 'Falta el tercero', 'Selecciona un proveedor o captura nombre+NIT.');
      return;
    }
    setSavingTercero(true);
    try {
      const resp = await cambiarTerceroGasto(gastoEditando._id, {
        proveedor: terceroForm.proveedor?._id || null,
        proveedorSinCrear: terceroForm.proveedor ? null : terceroForm.proveedorSinCrear
      });
      if (resp?.status === 200) {
        Alertassw('success', 'Tercero actualizado', 'El tercero del gasto fue vinculado correctamente.');
        setGastoEditando(null);
        cargar();
      }
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setSavingTercero(false);
    }
  };

  const handleVerificar = async () => {
    setVerificando(true);
    setReporteVerif(null);
    try {
      const resp = await verificarRelacion(id);
      if (resp?.status === 200) setReporteVerif(resp.data.data);
    } catch (err) {
      Alertassw('error', 'Error', extractError(err));
    } finally {
      setVerificando(false);
    }
  };

  const handleAprobar = async () => {
    setAprobando(true);
    setResultadoAprobacion(null);
    try {
      const resp = await aprobarRelacion(id, '');
      if (resp?.status === 200) {
        const data = resp.data?.data || {};
        setResultadoAprobacion(data.sincronizacion || { sincronizados: [], fallos: [] });
        cargar();
        Alertassw('success', 'Aprobación procesada', 'La relación fue aprobada. Revisa el resultado del sync.');
      }
    } catch (err) {
      // CONFIG_CONTABLE_INCOMPLETA: el helper muestra SwalAlert con la lista
      // de campos faltantes y navega a /ConfiguracionContable si el admin acepta.
      if (await manejarConfigFaltanteSi422(err, navigate)) {
        return;
      }
      const data = err?.response?.data;
      if (data?.codigo === 'CIERRE_VALIDACION') {
        Alertassw('warning', 'Faltan datos', data.error);
      } else {
        Alertassw('error', 'Error al aprobar', extractError(err));
      }
    } finally {
      setAprobando(false);
    }
  };

  // Render tercero: check verde / X roja / warning manual
  const renderTercero = (g) => {
    if (g.proveedor) {
      return (
        <small className="d-inline-flex align-items-center gap-1">
          <FaCheckCircle className="text-success" />
          {g.proveedor.nombre || g.proveedor.nit}
        </small>
      );
    }
    if (g.proveedorSinCrear?.nit || g.proveedorSinCrear?.nombre) {
      return (
        <small className="d-inline-flex align-items-center gap-1">
          <FaExclamationTriangle className="text-warning" />
          {g.proveedorSinCrear.nombre || ''} ({g.proveedorSinCrear.nit || 's/nit'})
        </small>
      );
    }
    return (
      <small className="d-inline-flex align-items-center gap-1">
        <FaTimesCircle className="text-danger" />
        Sin tercero
      </small>
    );
  };

  if (loading) {
    return <div className="text-center py-5"><Spinner animation="border" /></div>;
  }
  if (!relacion) {
    return <Alert variant="warning" className="m-4">Relación no encontrada.</Alert>;
  }

  const yaAprobada = relacion.estado !== 'BORRADOR';

  return (
    <Container fluid className="py-4">
      <Button variant="link" className="mb-2 p-0" onClick={() => navigate('/RevisionRelaciones')}>
        <FaArrowLeft className="me-1" /> Volver al listado
      </Button>

      {/* Cards resumen */}
      <Row className="mb-3 g-2">
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <FaClipboardCheck className="text-primary mb-2" size={26} />
              <h6 className="text-muted mb-1">Consecutivo</h6>
              <code>{relacion.consecutivo}</code>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted mb-1">Estado</h6>
              <Badge bg={ESTADO_BADGE[relacion.estado] || 'secondary'}>{relacion.estado}</Badge>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted mb-1">Total gastos</h6>
              <h4 className="fw-bold mb-0 text-danger">{formatoCOP(relacion.montoTotalGastos)}</h4>
              <small className="text-muted">{gastos.length} gasto(s)</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100">
            <Card.Body className="text-center w-100">
              <h6 className="text-muted mb-1">Responsable</h6>
              <strong>{relacion.responsable?.nombre || ''} {relacion.responsable?.apellido || ''}</strong>
            </Card.Body>
          </Card>
        </Col>
        {/* ── Cards nuevas ── */}
        {saldoAnterior > 0 && (
          <Col md={3}>
            <Card className="h-100 shadow-sm w-100 border-start border-secondary border-3">
              <Card.Body className="text-center w-100">
                <FaWallet className="text-secondary mb-1" size={20} />
                <h6 className="text-muted mb-1">Saldo anterior</h6>
                <h5 className="fw-bold mb-0 text-secondary">{formatoCOP(saldoAnterior)}</h5>
                <small className="text-muted">Del cierre previo</small>
              </Card.Body>
            </Card>
          </Col>
        )}
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100 border-start border-success border-3">
            <Card.Body className="text-center w-100">
              <FaArrowDown className="text-success mb-1" size={20} />
              <h6 className="text-muted mb-1">Entregas recibidas</h6>
              <h5 className="fw-bold mb-0 text-success">{formatoCOP(relacion.montoTotalEntregasRecibidas)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100 border-start border-info border-3">
            <Card.Body className="text-center w-100">
              <FaArrowDown className="text-info mb-1" size={20} />
              <h6 className="text-muted mb-1">Traslados recibidos</h6>
              <h5 className="fw-bold mb-0 text-info">{formatoCOP(relacion.montoTotalTrasladosRecibidos)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100 border-start border-warning border-3">
            <Card.Body className="text-center w-100">
              <FaArrowUp className="text-warning mb-1" size={20} />
              <h6 className="text-muted mb-1">Traslados enviados</h6>
              <h5 className="fw-bold mb-0 text-warning">{formatoCOP(relacion.montoTotalTrasladosEnviados)}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="h-100 shadow-sm w-100 border-start border-primary border-3">
            <Card.Body className="text-center w-100">
              <FaWallet className="text-primary mb-1" size={20} />
              <h6 className="text-muted mb-1">Saldo final bolsillo</h6>
              <h5 className={`fw-bold mb-0 ${relacion.saldoFinal >= 0 ? 'text-primary' : 'text-danger'}`}>
                {formatoCOP(relacion.saldoFinal)}
              </h5>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Reporte de Verificar */}
      {reporteVerif && (
        <Alert variant={reporteVerif.listoParaAprobar ? 'success' : 'warning'} className="mb-3">
          <strong>{reporteVerif.listoParaAprobar ? '✅ Listo para aprobar' : '⚠ Atención antes de aprobar'}</strong>
          {reporteVerif.bloqueos?.length > 0 && (
            <ul className="mb-1 mt-2">
              {reporteVerif.bloqueos.map((b, i) => (
                <li key={i}><strong>{b.codigo}:</strong> {b.descripcion}</li>
              ))}
            </ul>
          )}
          {reporteVerif.advertencias?.length > 0 && (
            <ul className="mb-1 mt-2">
              {reporteVerif.advertencias.map((a, i) => (
                <li key={i} className="text-muted">{a.descripcion}</li>
              ))}
            </ul>
          )}
          {reporteVerif.comprobantesSimulados?.length > 0 && (
            <div className="mt-2 small">
              <strong>Se generarían {reporteVerif.comprobantesSimulados.length} comprobante(s):</strong>{' '}
              {reporteVerif.comprobantesSimulados.map(c => `${c.mes} (${formatoCOP(c.totalDebito)})`).join(', ')}
            </div>
          )}
        </Alert>
      )}

      {/* Resultado de Aprobar */}
      {resultadoAprobacion && (
        <Card className="mb-3 shadow-sm w-100">
          <Card.Header className="bg-light w-100">
            <strong>Resultado del sync</strong>
          </Card.Header>
          <Card.Body className="w-100">
            {resultadoAprobacion.sincronizados?.length > 0 && (
              <div className="mb-2 small">
                <FaCheckCircle className="text-success me-1" />
                {resultadoAprobacion.sincronizados.length} comprobante(s) sincronizado(s):
                <ul className="mb-0">
                  {resultadoAprobacion.sincronizados.map((s, i) => (
                    <li key={i}>{s.consecutivo} — externalId: <code>{s.externalId || '—'}</code></li>
                  ))}
                </ul>
              </div>
            )}
            {resultadoAprobacion.fallos?.length > 0 && (
              <div className="small">
                <FaTimesCircle className="text-danger me-1" />
                {resultadoAprobacion.fallos.length} fallo(s):
                <ul className="mb-0">
                  {resultadoAprobacion.fallos.map((f, i) => (
                    <li key={i}>
                      <strong>{f.consecutivo || f.id}</strong>: {f.error}
                    </li>
                  ))}
                </ul>
                <Alert variant="warning" className="py-2 mt-2 mb-0">
                  Los comprobantes fallidos quedaron en estado SYNC_ERROR. El cron los reintentara automaticamente o puedes forzar el sync individualmente.
                </Alert>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Tabla de gastos */}
      <Card className="mb-3 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <strong>Gastos incluidos en la relación</strong>
        </Card.Header>
        <Card.Body className="p-0 w-100">
          <div className="table-responsive">
            <Table hover size="sm" className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Fecha</th>
                  <th>Categoría</th>
                  <th>Descripción</th>
                  <th>Tercero</th>
                  <th>Factura</th>
                  <th>OT</th>
                  <th className="text-end">Valor</th>
                  <th className="text-center">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {gastos.map(g => (
                  <tr key={g._id}>
                    <td><small>{new Date(g.fecha).toLocaleDateString('es-CO')}</small></td>
                    <td><small>{g.categoria}</small></td>
                    <td><small>{g.descripcion}</small></td>
                    <td>{renderTercero(g)}</td>
                    <td>
                      {g.tieneFactura
                        ? <Badge bg="primary">{g.numeroFactura || 'sí'}</Badge>
                        : <small className="text-muted">—</small>}
                    </td>
                    <td><small className="text-muted">{g.ordenTrabajo?.numeroOT || '—'}</small></td>
                    <td className="text-end fw-bold">{formatoCOP(g.valorTotal)}</td>
                    <td className="text-center">
                      <Button
                        size="sm"
                        variant="outline-warning"
                        onClick={() => abrirEdicionTercero(g)}
                        title="Editar SOLO el tercero (no precios)"
                        disabled={yaAprobada}
                      >
                        <FaUserEdit /> Tercero
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>

      {/* ── Entregas recibidas ── */}
      {entregas.length > 0 && (
        <Card className="mb-3 shadow-sm w-100">
          <Card.Header className="bg-light w-100">
            <strong><FaWallet className="me-2 text-success" />Entregas recibidas ({entregas.length})</strong>
          </Card.Header>
          <Card.Body className="p-0 w-100">
            <div className="table-responsive">
              <Table hover size="sm" className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Fecha</th>
                    <th>Consecutivo</th>
                    <th>Cuenta origen</th>
                    <th>Entregado por</th>
                    <th className="text-end">Monto</th>
                    <th>Estado</th>
                    <th>Observaciones</th>
                  </tr>
                </thead>
                <tbody>
                  {entregas.map(e => {
                    const obs = e.observaciones || e.confirmacion?.observacionesReceptor || '—';
                    const entregadoPor = e.entregadoPor
                      ? `${e.entregadoPor.nombre || ''} ${e.entregadoPor.apellido || ''}`.trim()
                      : '—';
                    return (
                      <tr key={e._id}>
                        <td><small>{new Date(e.fecha).toLocaleDateString('es-CO')}</small></td>
                        <td><small className="text-muted">{e.consecutivo || '—'}</small></td>
                        <td><small>{e.cuentaOrigen?.nombre || '—'}</small></td>
                        <td><small>{entregadoPor}</small></td>
                        <td className="text-end fw-bold text-success">+{formatoCOP(e.monto)}</td>
                        <td>
                          <Badge bg={e.estado === 'CONFIRMADA' ? 'success' : 'secondary'}>
                            {e.estado}
                          </Badge>
                        </td>
                        <td><small className="text-muted">{obs}</small></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="table-light fw-bold">
                  <tr>
                    <td colSpan={4}>Total entregas</td>
                    <td className="text-end text-success">
                      +{formatoCOP(entregas.reduce((s, e) => s + (e.monto || 0), 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </Table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* ── Traslados recibidos ── */}
      {(() => {
        const bolsilloId = String(relacion.bolsillo?._id || relacion.bolsillo || '');
        const recibidos = traslados.filter(t => String(t.cuenta_destino?._id || t.cuenta_destino) === bolsilloId);
        if (!recibidos.length) return null;
        return (
          <Card className="mb-3 shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <strong><FaArrowDown className="me-2 text-info" />Traslados recibidos ({recibidos.length})</strong>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Consecutivo</th>
                      <th>Enviado desde</th>
                      <th className="text-end">Monto</th>
                      <th>Estado</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recibidos.map(t => (
                      <tr key={t._id}>
                        <td><small>{new Date(t.fecha).toLocaleDateString('es-CO')}</small></td>
                        <td><small className="text-muted">{t.consecutivo || '—'}</small></td>
                        <td><small>{t.cuenta_origen?.nombre || '—'}</small></td>
                        <td className="text-end fw-bold text-info">+{formatoCOP(t.monto)}</td>
                        <td>
                          <Badge bg={t.estado === 'CONFIRMADO' ? 'success' : 'secondary'}>
                            {t.estado}
                          </Badge>
                        </td>
                        <td><small className="text-muted">{t.confirmacion?.observaciones || t.rechazo?.motivoRechazo || '—'}</small></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td colSpan={3}>Total recibido</td>
                      <td className="text-end text-info">+{formatoCOP(recibidos.reduce((s, t) => s + (t.monto || 0), 0))}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>
        );
      })()}

      {/* ── Traslados enviados ── */}
      {(() => {
        const bolsilloId = String(relacion.bolsillo?._id || relacion.bolsillo || '');
        const enviados = traslados.filter(t => String(t.cuenta_origen?._id || t.cuenta_origen) === bolsilloId);
        if (!enviados.length) return null;
        return (
          <Card className="mb-3 shadow-sm w-100">
            <Card.Header className="bg-light w-100">
              <strong><FaArrowUp className="me-2 text-warning" />Traslados enviados ({enviados.length})</strong>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              <div className="table-responsive">
                <Table hover size="sm" className="mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Fecha</th>
                      <th>Consecutivo</th>
                      <th>Enviado a</th>
                      <th className="text-end">Monto</th>
                      <th>Estado</th>
                      <th>Observaciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {enviados.map(t => (
                      <tr key={t._id}>
                        <td><small>{new Date(t.fecha).toLocaleDateString('es-CO')}</small></td>
                        <td><small className="text-muted">{t.consecutivo || '—'}</small></td>
                        <td><small>{t.cuenta_destino?.nombre || '—'}</small></td>
                        <td className="text-end fw-bold text-warning">-{formatoCOP(t.monto)}</td>
                        <td>
                          <Badge bg={t.estado === 'CONFIRMADO' ? 'success' : 'secondary'}>
                            {t.estado}
                          </Badge>
                        </td>
                        <td><small className="text-muted">{t.confirmacion?.observaciones || t.rechazo?.motivoRechazo || '—'}</small></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="table-light fw-bold">
                    <tr>
                      <td colSpan={3}>Total enviado</td>
                      <td className="text-end text-warning">-{formatoCOP(enviados.reduce((s, t) => s + (t.monto || 0), 0))}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>
        );
      })()}

      {/* ── Cuadre contable ── */}
      {(() => {
        const totalEntradas = saldoAnterior
          + (relacion.montoTotalEntregasRecibidas || 0)
          + (relacion.montoTotalTrasladosRecibidos || 0);
        const totalSalidas  = (relacion.montoTotalGastos || 0)
          + (relacion.montoTotalTrasladosEnviados || 0);
        const saldoFinal    = totalEntradas - totalSalidas;
        return (
          <Card className="mb-3 shadow-sm w-100 border-primary">
            <Card.Header className="bg-primary text-white w-100">
              <strong>Cuadre contable del periodo</strong>
            </Card.Header>
            <Card.Body className="p-0 w-100">
              <Table size="sm" className="mb-0">
                <tbody>
                  {saldoAnterior > 0 && (
                    <tr>
                      <td className="ps-3 text-muted">Saldo anterior (del cierre previo)</td>
                      <td className="text-end pe-3 text-secondary fw-bold">{formatoCOP(saldoAnterior)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="ps-3 text-success">+ Entregas recibidas</td>
                    <td className="text-end pe-3 text-success fw-bold">+{formatoCOP(relacion.montoTotalEntregasRecibidas)}</td>
                  </tr>
                  {relacion.montoTotalTrasladosRecibidos > 0 && (
                    <tr>
                      <td className="ps-3 text-info">+ Traslados recibidos</td>
                      <td className="text-end pe-3 text-info fw-bold">+{formatoCOP(relacion.montoTotalTrasladosRecibidos)}</td>
                    </tr>
                  )}
                  {relacion.montoTotalTrasladosEnviados > 0 && (
                    <tr>
                      <td className="ps-3 text-warning">− Traslados enviados</td>
                      <td className="text-end pe-3 text-warning fw-bold">-{formatoCOP(relacion.montoTotalTrasladosEnviados)}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="ps-3 text-danger">− Gastado</td>
                    <td className="text-end pe-3 text-danger fw-bold">-{formatoCOP(relacion.montoTotalGastos)}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr className="table-primary fw-bold fs-6">
                    <td className="ps-3">= Saldo final</td>
                    <td className={`text-end pe-3 ${saldoFinal >= 0 ? 'text-primary' : 'text-danger'}`}>
                      {formatoCOP(saldoFinal)}
                    </td>
                  </tr>
                </tfoot>
              </Table>
            </Card.Body>
          </Card>
        );
      })()}

      {/* Acciones del cierre */}
      {!yaAprobada && (
        <Row className="g-2">
          {/*<Col xs="auto">
            <Button
              variant="outline-info"
              onClick={handleVerificar}
              disabled={verificando}
            >
              {verificando ? <Spinner animation="border" size="sm" /> : <><FaCheck className="me-1" /> Verificar</>}
            </Button>
          </Col>*/}
          <Col xs="auto">
          {userData?.role?.permisos?.indexOf(APPROVE_RELACIONES) !== -1 && (
            <Button
              variant="success"
              size="lg"
              onClick={handleAprobar}
              disabled={aprobando}
            >
              {aprobando ? 'Aprobando...' : <><FaPlayCircle className="me-1" /> Aprobar y sincronizar</>}
            </Button>
          )}
          </Col>
        </Row>
      )}

      {/* Modal cambiar tercero */}
      <Modal show={!!gastoEditando} onHide={() => setGastoEditando(null)} size="lg" centered scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Editar tercero del gasto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {gastoEditando && (
            <>
              <Alert variant="light" className="border small">
                <strong>{gastoEditando.descripcion}</strong>{' '}
                <code className="ms-2">{formatoCOP(gastoEditando.valorTotal)}</code>
                <div className="text-muted">
                  Solo se actualizara el tercero. Los demas datos del gasto quedan intactos.
                </div>
              </Alert>
              <SelectorProveedorTercero
                value={terceroForm}
                onChange={(v) => setTerceroForm(v)}
              />
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setGastoEditando(null)} disabled={savingTercero}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={guardarTercero} disabled={savingTercero}>
            {savingTercero ? 'Guardando...' : 'Guardar tercero'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default RevisionRelacionDetalle;
